import { createSlice } from '@reduxjs/toolkit';
import { GriResidence, GriServer, SyncRecord } from 'services/api';
import { getResidencesByGroupAsync, getServersByGroupAsync, getSyncHistoryAsync, getSyncHistoryDetailAsync, postResidenceSyncAsync, putResidenceAsync, putServerAsync } from './asyncActions';

interface HistoryEntry {
  data: SyncRecord[] | null;
  status: 'idle' | 'loading' | 'failed';
}

interface ResiplusIntegrationState {
  residences: { data: GriResidence[] | null; status: 'idle' | 'loading' | 'failed' };
  servers: { data: GriServer[] | null; status: 'idle' | 'loading' | 'failed' };
  residenceSaveStatus: 'idle' | 'loading' | 'failed';
  serverSaveStatus: 'idle' | 'loading' | 'failed';
  syncStatus: Record<string, 'idle' | 'loading' | 'failed'>;
  syncHistory: Record<string, HistoryEntry>;
  syncHistoryDetail: { data: SyncRecord | null; status: 'idle' | 'loading' | 'failed' };
}

const initialState: ResiplusIntegrationState = {
  residences: { data: null, status: 'idle' },
  servers: { data: null, status: 'idle' },
  residenceSaveStatus: 'idle',
  serverSaveStatus: 'idle',
  syncStatus: {},
  syncHistory: {},
  syncHistoryDetail: { data: null, status: 'idle' },
};

export const resiplusIntegrationSlice = createSlice({
  name: 'resiplusIntegration',
  initialState,
  reducers: {
    resetResiplusIntegration: () => initialState,
    clearSyncHistoryDetail: (state) => {
      state.syncHistoryDetail = { data: null, status: 'idle' };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getResidencesByGroupAsync.pending, (state) => {
        state.residences.status = 'loading';
      })
      .addCase(getResidencesByGroupAsync.fulfilled, (state, action) => {
        state.residences.status = 'idle';
        state.residences.data = action.payload ?? null;
      })
      .addCase(getResidencesByGroupAsync.rejected, (state) => {
        state.residences.status = 'failed';
      })
      .addCase(postResidenceSyncAsync.pending, (state, action) => {
        state.syncStatus[action.meta.arg] = 'loading';
      })
      .addCase(postResidenceSyncAsync.fulfilled, (state, action) => {
        state.syncStatus[action.meta.arg] = 'idle';
      })
      .addCase(postResidenceSyncAsync.rejected, (state, action) => {
        state.syncStatus[action.meta.arg] = 'failed';
      })
      .addCase(getServersByGroupAsync.pending, (state) => {
        state.servers.status = 'loading';
      })
      .addCase(getServersByGroupAsync.fulfilled, (state, action) => {
        state.servers.status = 'idle';
        state.servers.data = action.payload;
      })
      .addCase(getServersByGroupAsync.rejected, (state) => {
        state.servers.status = 'failed';
      })
      .addCase(putResidenceAsync.pending, (state) => {
        state.residenceSaveStatus = 'loading';
      })
      .addCase(putResidenceAsync.fulfilled, (state, action) => {
        state.residenceSaveStatus = 'idle';
        if (state.residences.data && action.payload) {
          state.residences.data = state.residences.data.map((r) =>
            r.id === action.payload.id ? action.payload : r,
          );
        }
      })
      .addCase(putResidenceAsync.rejected, (state) => {
        state.residenceSaveStatus = 'failed';
      })
      .addCase(putServerAsync.pending, (state) => {
        state.serverSaveStatus = 'loading';
      })
      .addCase(putServerAsync.fulfilled, (state, action) => {
        state.serverSaveStatus = 'idle';
        if (state.servers.data && action.payload) {
          state.servers.data = state.servers.data.map((s) =>
            s.id === action.payload.id ? action.payload : s,
          );
        }
      })
      .addCase(putServerAsync.rejected, (state) => {
        state.serverSaveStatus = 'failed';
      })
      .addCase(getSyncHistoryAsync.pending, (state, action) => {
        const id = action.meta.arg.residenceId;
        state.syncHistory[id] = { data: null, status: 'loading' };
      })
      .addCase(getSyncHistoryAsync.fulfilled, (state, action) => {
        const id = action.meta.arg.residenceId;
        state.syncHistory[id] = { data: action.payload, status: 'idle' };
      })
      .addCase(getSyncHistoryAsync.rejected, (state, action) => {
        const id = action.meta.arg.residenceId;
        state.syncHistory[id] = { data: null, status: 'failed' };
      })
      .addCase(getSyncHistoryDetailAsync.pending, (state) => {
        state.syncHistoryDetail = { data: null, status: 'loading' };
      })
      .addCase(getSyncHistoryDetailAsync.fulfilled, (state, action) => {
        state.syncHistoryDetail = { data: action.payload, status: 'idle' };
      })
      .addCase(getSyncHistoryDetailAsync.rejected, (state) => {
        state.syncHistoryDetail = { data: null, status: 'failed' };
      });
  },
});

export const { resetResiplusIntegration, clearSyncHistoryDetail } = resiplusIntegrationSlice.actions;
export default resiplusIntegrationSlice.reducer;
